"use client";

import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { truncateDecimals } from "../utils/numberFormatter";
import { Button } from "./ui/button";
import {
  TrendingUp,
  TrendingDown,
  Search,
  AlertCircle,
  ArrowRight,
} from "lucide-react";
import {
  useMarketAssets,
  useFeatureFlagsByCategory,
} from "../hooks/query/queries";
import { Skeleton } from "./ui/skeleton";
import { useNavigate } from "react-router-dom";
import CurrencyFormatter from "../utils/currencyFormatter";
import { useCurrencySelection } from "../context/CurrencySelectionContext";
import newly from "../assets/images/dashboard/hands.svg";
import stablecoins from "../assets/images/dashboard/coins.svg";
import rewards from "../assets/images/dashboard/rewards.svg";

const SvgIcon = ({ src, className, isSelected }) => {
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const colorFilter = isSelected
    ? "brightness(0) invert(1)"
    : "brightness(0) saturate(100%) invert(42%) sepia(8%) saturate(1234%) hue-rotate(218deg) brightness(95%) contrast(87%)";

  if (error || isLoading || !src) {
    return (
      <img
        src={src}
        alt=""
        className={className}
        style={{
          filter: colorFilter,
          opacity: isLoading ? 0.5 : 1,
          display: "block",
          flexShrink: 0,
        }}
        onError={() => {
          setError(true);
          setIsLoading(false);
        }}
        onLoad={() => setIsLoading(false)}
      />
    );
  }

  return (
    <img
      src={src}
      alt=""
      className={className}
      style={{
        filter: colorFilter,
        display: "block",
        flexShrink: 0,
      }}
      onError={() => setError(true)}
    />
  );
};

const AssetImage = ({ src, alt, assetName, asset }) => {
  const [imageError, setImageError] = useState(false);
  const [imageSrc, setImageSrc] = useState(src);

  useEffect(() => {
    setImageSrc(src);
    setImageError(false);
  }, [src]);

  const handleError = () => {
    setImageError(true);
  };

  if (imageError || !imageSrc) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-400 rounded-full">
        <span className="text-xs font-semibold">
          {(assetName || asset || "?").charAt(0).toUpperCase()}
        </span>
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt || `${assetName || asset} icon`}
      className="w-full h-full object-cover rounded-full"
      onError={handleError}
    />
  );
};

const filterOptions = [
  { id: "all", label: "All", icon: rewards, isSvg: true },
  { id: "popular", label: "Most Popular", icon: rewards, isSvg: true },
  { id: "gainers", label: "Gainers", icon: TrendingUp, isSvg: false },
  { id: "losers", label: "Losers", icon: TrendingDown, isSvg: false },
  { id: "stablecoins", label: "Stablecoins", icon: stablecoins, isSvg: true },
  { id: "newly", label: "Newly Listed", icon: newly, isSvg: true },
];

export default function MarketDashboard({ fromDashboard = true }) {
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const { data: marketAssets, isLoading, isError, error } = useMarketAssets();
  const { data: featureFlags } = useFeatureFlagsByCategory("mainFeatures");
  const navigate = useNavigate();
  const { selectedCurrency, isCrypto, isInProvider } = useCurrencySelection();

  const effectiveCurrency = isInProvider
    ? isCrypto
      ? "USD"
      : selectedCurrency
    : "USD";
  const effectiveIsCrypto = isInProvider ? isCrypto : true;

  const formatMarketCap = (value, currency = "USD") => {
    if (!value && value !== 0) {
      return currency === "ZAR" ? "R 0.00" : "0 USD";
    }
    const num = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(num)) {
      return currency === "ZAR" ? "R 0.00" : "0 USD";
    }

    const formatterInstance = new CurrencyFormatter({
      locale: currency === "ZAR" ? "en-ZA" : "en-US",
      decimalPlaces: 2,
    });

    if (currency === "ZAR") {
      if (num >= 1e12) {
        const trillions = num / 1e12;
        return `R ${truncateDecimals(trillions, 2)}T`;
      }
      if (num >= 1e9) {
        const billions = num / 1e9;
        return `R ${truncateDecimals(billions, 2)}B`;
      }
      if (num >= 1e6) {
        const millions = num / 1e6;
        return `R ${truncateDecimals(millions, 2)}M`;
      }
      return formatterInstance.formatFiat(num, "ZAR");
    }

    if (num >= 1e12) {
      const trillions = num / 1e12;
      const formatted = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(trillions);
      return `${formatted}T USD`;
    }
    if (num >= 1e9) {
      const billions = num / 1e9;
      const formatted = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(billions);
      return `${formatted}B USD`;
    }
    if (num >= 1e6) {
      const millions = num / 1e6;
      const formatted = new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(millions);
      return `${formatted}M USD`;
    }

    const formatted = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);

    return `${formatted} USD`;
  };

  const formatPriceUSD = (price) => {
    if (!price && price !== 0) {
      return "0 USD";
    }
    const numPrice =
      typeof price === "number"
        ? price
        : parseFloat(String(price).replace(/[^0-9.-]/g, "")) || 0;

    if (isNaN(numPrice)) {
      return "0 USD";
    }

    const formatted = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numPrice);

    return `${formatted.replace(/,/g, " ")} USD`;
  };

  const formatPercentage = (percent) => {
    if (!percent && percent !== 0) return "0.00%";
    const isPositive = percent >= 0;
    return `${isPositive ? "+" : ""}${truncateDecimals(percent, 2)}%`;
  };

  const getProcessedAssets = () => {
    if (!marketAssets) return [];

    return Object.entries(marketAssets).map(([, data]) => {
      const originalPriceInZar = data.priceInZar || data.info?.zarprice;
      const hasRealZarPrice =
        !!originalPriceInZar && originalPriceInZar !== data.price;

      const processedAsset = {
        symbol: data.symbol,
        asset: data.info?.asset || data.symbol.toLowerCase(),
        assetName: data.info?.assetName || data.symbol,
        image: data.info?.image,
        price: data.price,
        priceInZar: originalPriceInZar || data.price,
        hasRealZarPrice,
        volume_24h: data.volume_24h,
        percent_change_24h: data.percent_change_24h,
        percent_change_7d: data.percent_change_7d,
        market_cap: data.market_cap,
        market_cap_zar: data.market_cap_zar,
        assetId: data.info?.assetId,
        _id: data.info?._id,
        createdAt: data.info?.createdAt,
      };

      return processedAsset;
    });
  };

  const processedAssets = getProcessedAssets();

  const usdToZarRate = useMemo(() => {
    const assetsWithZarPrice = processedAssets.filter(
      (asset) => asset.hasRealZarPrice && asset.price && asset.price !== 0
    );

    if (assetsWithZarPrice.length > 0) {
      const rates = assetsWithZarPrice.map(
        (asset) => asset.priceInZar / asset.price
      );
      const avgRate = rates.reduce((sum, rate) => sum + rate, 0) / rates.length;
      return avgRate;
    }

    const defaultRate = 18.5;
    return defaultRate;
  }, [processedAssets]);

  const getFilteredAssets = () => {
    let filtered = processedAssets;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = processedAssets.filter(
        (asset) =>
          asset.assetName?.toLowerCase().includes(search) ||
          asset.symbol?.toLowerCase().includes(search) ||
          asset.asset?.toLowerCase().includes(search)
      );
    }

    switch (selectedFilter) {
      case "popular":
        return filtered.sort(
          (a, b) => (b.volume_24h || 0) - (a.volume_24h || 0)
        );

      case "gainers":
        return filtered.sort(
          (a, b) => b.percent_change_24h - a.percent_change_24h
        );

      case "losers":
        return filtered.sort(
          (a, b) => a.percent_change_24h - b.percent_change_24h
        );

      case "stablecoins":
        return filtered.filter((asset) =>
          ["USDT", "USDC", "DAI", "USDD"].includes(asset.symbol)
        );

      case "newly":
        return filtered.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA;
        });

      case "all":
      default:
        return filtered.sort(
          (a, b) => (b.market_cap || 0) - (a.market_cap || 0)
        );
    }
  };

  const displayAssets = getFilteredAssets();

  const handleBuy = (asset) => {
    navigate("/trading/digitalWallet", {
      state: {
        tabActive: "Buy",
        selectedTab: "Buy",
        selectedCrypto: asset,
        type: "buy-sell",
      },
    });
  };

  const handleAssetClick = (asset) => {
    navigate("/trading/asset-dashboard", { state: asset });
  };

  return (
    <div className="flex-1">
      <Card className="flex flex-col">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="text-[20px] font-semibold text-[#6D6584]">
              Market
            </CardTitle>
            {!fromDashboard && (
              <div className="relative w-[306px] h-[56px] mt-2 mb-4 md:block hidden">
                <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#6D6584]" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full h-[56px] pl-12 pr-4 rounded-full border border-[#EBE9EF] bg-transparent text-sm text-[#6D6584] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#33125F]/20 focus:border-[#33125F]"
                />
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="p-0 flex flex-col flex-1">
          <div className="px-3 md:px-6 pb-4 flex gap-2 flex-wrap">
            {filterOptions.map((filter) => {
              const Icon = filter.icon;
              const isSelected = selectedFilter === filter.id;

              return (
                <Button
                  key={filter.id}
                  onClick={() =>
                    !filter.disabled && setSelectedFilter(filter.id)
                  }
                  variant={isSelected ? "default" : "outline"}
                  disabled={filter.disabled}
                  className={`flex items-center gap-2 rounded-full h-[40px] md:h-[48px] ${
                    isSelected
                      ? "bg-[#33125F] text-white border-[#6D6584] hover:bg-[#33125F] hover:opacity-90"
                      : "bg-white text-[#423E51] hover:opacity-90"
                  } ${filter.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {filter.isSvg ? (
                    <SvgIcon
                      src={filter.icon}
                      className="h-3 w-3 md:h-4 md:w-4 flex-shrink-0"
                      isSelected={isSelected}
                    />
                  ) : (
                    <Icon className="h-3 w-3 md:h-4 md:w-4" />
                  )}
                  <span className="text-xs md:text-sm font-medium">
                    {filter.label}
                  </span>
                </Button>
              );
            })}
          </div>

          <div className="mt-4 md:hidden px-3 pb-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-[#6D6584]">Asset</div>
              <div className="text-sm font-semibold text-[#6D6584] text-right w-[70%]">
                Price
              </div>
              {featureFlags?.buyCrypto && <div className="w-1"></div>}
            </div>
          </div>

          <div className="mt-5 hidden md:block px-6 pb-3 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div className="text-sm font-semibold text-[#6D6584] w-[30%]">
                Asset
              </div>
              <div className="text-sm font-semibold text-[#6D6584] text-left w-[18%]">
                Price
              </div>
              <div className="text-sm font-semibold text-[#6D6584] text-left w-[19%]">
                Change (24hr)
              </div>
              <div className="text-sm font-semibold text-[#6D6584] text-left w-[18%]">
                Market Cap
              </div>
              {featureFlags?.buyCrypto && <div className="w-[15%]"></div>}
            </div>
          </div>

          <div
            className={`flex-1 overflow-y-auto ${fromDashboard ? "min-h-[400px] max-h-[400px]" : "min-h-[600px] max-h-[600px]"}`}
          >
            {isLoading ? (
              <div className="space-y-4 px-3 md:px-6 py-4">
                {[...Array(5)].map((_, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between md:flex-row md:items-center gap-3 md:gap-4"
                  >
                    <Skeleton className="h-12 bg-gray-200 flex-1 md:flex-none md:w-[30%]" />
                    <Skeleton className="h-12 bg-gray-200 w-20 md:w-[18%]" />
                    <Skeleton className="hidden md:block h-12 bg-gray-200 md:w-[18%]" />
                    <Skeleton className="hidden md:block h-12 bg-gray-200 md:w-[18%]" />
                    <Skeleton className="h-8 w-8 bg-gray-200 md:w-[16%]" />
                  </div>
                ))}
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <AlertCircle className="h-12 w-12 text-rose-500 mb-4" />
                <h3 className="text-lg font-semibold text-[#423E51] mb-2">
                  Failed to load market data
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  {error?.message ||
                    "An error occurred while fetching market data. Please try again later."}
                </p>
                <Button
                  variant="outline"
                  onClick={() => window.location.reload()}
                  className="rounded-full"
                >
                  Retry
                </Button>
              </div>
            ) : displayAssets.length > 0 ? (
              <div>
                {displayAssets.map((asset, index) => {
                  const isPositiveChange = asset.percent_change_24h >= 0;
                  return (
                    <div
                      key={asset.symbol || index}
                      className="flex items-center justify-between md:flex-row md:items-center px-3 md:px-6 py-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3 md:w-[30%] flex-1 md:flex-none">
                        <div className="relative w-10 h-10 rounded-full flex-shrink-0 bg-gray-100 flex items-center justify-center overflow-hidden">
                          <AssetImage
                            src={asset.image}
                            alt={`${asset.assetName || asset.symbol} icon`}
                            assetName={asset.assetName}
                            asset={asset.symbol}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-[#423E51] text-sm md:text-base">
                            {asset.assetName}
                          </div>
                          <div className="text-xs md:text-sm text-gray-500">
                            {asset.symbol}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col items-end md:hidden gap-1">
                        <div className="font-semibold text-[#6D6584] text-sm">
                          {formatPriceUSD(asset.price)}
                        </div>
                        <div
                          className={`font-semibold text-sm ${
                            isPositiveChange
                              ? "text-emerald-500"
                              : "text-rose-500"
                          }`}
                        >
                          {formatPercentage(asset.percent_change_24h)}
                        </div>
                      </div>

                      <div className="hidden md:block md:w-[19%] text-left">
                        <div className="font-semibold text-[#6D6584] text-base ">
                          {formatPriceUSD(asset.price)}
                        </div>
                      </div>

                      <div className="hidden md:block md:w-[19%] text-left">
                        <div
                          className={`font-semibold text-base ${
                            isPositiveChange
                              ? "text-emerald-500"
                              : "text-rose-500"
                          }`}
                        >
                          {formatPercentage(asset.percent_change_24h)}
                        </div>
                      </div>

                      <div className="hidden md:block md:w-[17%] text-left">
                        <div className="font-semibold text-[#6D6584] text-base">
                          {(() => {
                            let marketCapValue;

                            if (
                              !effectiveIsCrypto &&
                              effectiveCurrency === "ZAR"
                            ) {
                              if (
                                asset.market_cap_zar &&
                                asset.market_cap_zar !== asset.market_cap
                              ) {
                                marketCapValue = asset.market_cap_zar;
                              } else if (
                                asset.hasRealZarPrice &&
                                asset.price &&
                                asset.priceInZar &&
                                asset.market_cap &&
                                asset.price !== 0
                              ) {
                                const exchangeRate =
                                  asset.priceInZar / asset.price;
                                marketCapValue =
                                  asset.market_cap * exchangeRate;
                              } else if (asset.market_cap && usdToZarRate) {
                                marketCapValue =
                                  asset.market_cap * usdToZarRate;
                              } else {
                                marketCapValue = asset.market_cap;
                              }
                            } else {
                              marketCapValue = asset.market_cap;
                            }

                            return formatMarketCap(
                              marketCapValue,
                              effectiveCurrency
                            );
                          })()}
                        </div>
                      </div>

                      {featureFlags?.buyCrypto && (
                        <div className="flex md:flex-1 items-center gap-2 md:w-[16%] justify-end ">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleBuy(asset)}
                            className="hidden md:flex border-gray-200 bg-white text-[#423E51] hover:bg-gray-50 rounded-full px-5 text-sm h-9"
                          >
                            Buy
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleAssetClick(asset)}
                            className="border-gray-200 bg-white text-[#423E51] hover:bg-gray-50 h-8 w-8 md:h-9 md:w-9 rounded-full ml-2 md:ml-0"
                          >
                            <ArrowRight className="h-3 w-3 md:h-4 md:w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <Search className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-[#423E51] mb-2">
                  No assets found
                </h3>
                <p className="text-sm text-gray-500">
                  {searchTerm
                    ? `No assets match "${searchTerm}". Try a different search term.`
                    : "No assets are currently available in the market."}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
